<template>
  <div
    v-if="!isCapacitorMode()"
    class="!fixed !inset-0 !bg-gradient-to-br !from-indigo-500 !to-purple-600 !flex !items-center !justify-center !p-8 !z-[9999]"
  >
    <div
      class="!bg-white !rounded-2xl !p-8 !max-w-2xl !w-full !shadow-2xl !text-center max-h-[90vh] overflow-auto"
    >
      <div class="!mb-8">
        <span class="!text-6xl">🚫</span>
      </div>

      <h1 class="!text-3xl !font-bold !text-gray-900 !mb-6">
        Localisation requise
      </h1>

      <p class="!text-lg !text-gray-600 !leading-relaxed !mb-8">
        {{ description }}
      </p>

      <div
        class="!bg-gray-50 !rounded-xl !p-6 !mb-8 !text-left !border !border-gray-200"
      >
        <h3
          class="!text-xl !font-semibold !text-gray-900 !mb-6 !flex !items-center !gap-3"
        >
          <span class="!text-2xl">⚙️</span>
          Comment réactiver la localisation
        </h3>

        <div class="!space-y-6">
          <div v-if="browserInfo.isIOS" class="!space-y-4">
            <h4
              class="!text-lg !font-semibold !text-gray-800 !flex !items-center !gap-3"
            >
              <span class="!text-xl">📱</span>
              iOS
            </h4>
            <ol class="!space-y-2 !pl-6 !list-decimal">
              <li v-if="browserInfo.isSafari" class="!text-gray-600">
                Allez dans <strong>Réglages</strong> >
                <strong>Confidentialité</strong> >
                <strong>Services de localisation</strong>
              </li>
              <li v-else class="!text-gray-600">
                Allez dans <strong>Réglages</strong> >
                <strong>[Votre navigateur]</strong> >
                <strong>Localisation</strong>
              </li>
              <li class="!text-gray-600">
                Activez <strong>Autoriser</strong> ou
                <strong>Pendant l'utilisation de l'app</strong>
              </li>
              <li class="!text-gray-600">
                Revenez sur cette page et actualisez
              </li>
            </ol>
          </div>

          <div v-else-if="browserInfo.isAndroid" class="!space-y-4">
            <h4
              class="!text-lg !font-semibold !text-gray-800 !flex !items-center !gap-3"
            >
              <span class="!text-xl">📱</span>
              Android
            </h4>
            <ol
              v-if="browserInfo.isChrome"
              class="!space-y-2 !pl-6 !list-decimal"
            >
              <li class="!text-gray-600">
                Appuyez sur <strong>⋮</strong> > <strong>Paramètres</strong>
              </li>
              <li class="!text-gray-600">
                Allez dans <strong>Paramètres du site</strong> >
                <strong>Localisation</strong>
              </li>
              <li class="!text-gray-600">
                Sélectionnez <strong>Autoriser</strong>
              </li>
              <li class="!text-gray-600">Rechargez cette page</li>
            </ol>
            <ol v-else class="!space-y-2 !pl-6 !list-decimal">
              <li class="!text-gray-600">
                Allez dans les <strong>paramètres</strong> de votre navigateur
              </li>
              <li class="!text-gray-600">
                Cherchez les <strong>permissions de site</strong> ou
                <strong>localisation</strong>
              </li>
              <li class="!text-gray-600">
                Activez la localisation pour ce site
              </li>
              <li class="!text-gray-600">Rechargez cette page</li>
            </ol>
          </div>

          <div v-else class="!space-y-4">
            <h4
              class="!text-lg !font-semibold !text-gray-800 !flex !items-center !gap-3"
            >
              <span class="!text-xl">🖥️</span>
              Ordinateur
            </h4>
            <div v-if="browserInfo.isChrome" class="!space-y-2">
              <ol class="!space-y-2 !pl-6 !list-decimal">
                <li class="!text-gray-600">
                  Cliquez sur l'icône <strong>🔒</strong> dans la barre
                  d'adresse
                </li>
                <li class="!text-gray-600">
                  Cherchez <strong>Localisation</strong>
                </li>
                <li class="!text-gray-600">
                  Changez à <strong>Autoriser</strong>
                </li>
                <li class="!text-gray-600">Rechargez la page</li>
              </ol>
            </div>
            <div v-else-if="browserInfo.isSafari" class="!space-y-2">
              <ol class="!space-y-2 !pl-6 !list-decimal">
                <li class="!text-gray-600">
                  Allez dans <strong>Safari</strong> >
                  <strong>Préférences</strong>
                </li>
                <li class="!text-gray-600">
                  Cliquez sur <strong>Sites web</strong> >
                  <strong>Localisation</strong>
                </li>
                <li class="!text-gray-600">
                  Sélectionnez <strong>Autoriser</strong> pour ce site
                </li>
                <li class="!text-gray-600">Rechargez la page</li>
              </ol>
            </div>
            <div v-else-if="browserInfo.isFirefox" class="!space-y-2">
              <ol class="!space-y-2 !pl-6 !list-decimal">
                <li class="!text-gray-600">
                  Cliquez sur l'icône <strong>🔒</strong> dans la barre
                  d'adresse
                </li>
                <li class="!text-gray-600">
                  Cherchez <strong>Permissions</strong> >
                  <strong>Accéder à la localisation</strong>
                </li>
                <li class="!text-gray-600">
                  Changez à <strong>Autoriser</strong>
                </li>
                <li class="!text-gray-600">Rechargez la page</li>
              </ol>
            </div>
          </div>
        </div>

        <div class="!mt-8 !pt-6 !border-t !border-gray-200">
          <h4
            class="!text-lg !font-semibold !text-gray-800 !mb-4 !flex !items-center !gap-3"
          >
            <span class="!text-xl">ℹ️</span>
            Conseils utiles
          </h4>
          <ul class="!space-y-2 !pl-6 !list-disc">
            <li class="!text-gray-600">
              Assurez-vous que le GPS de votre appareil est activé
            </li>
            <li class="!text-gray-600">
              Vérifiez que vous êtes bien connecté à internet
            </li>
            <li class="!text-gray-600">
              Certains réseaux d'entreprise bloquent la localisation
            </li>
          </ul>
        </div>
      </div>

      <div class="!space-y-4">
        <button
          type="button"
          class="!w-full !flex !items-center !justify-center !gap-3 !px-8 !py-4 !bg-blue-600 !text-white !rounded-xl !font-semibold !text-lg !transition-colors !border-0 !hover:bg-blue-700 !cursor-pointer !disabled:opacity-50 !disabled:cursor-not-allowed"
          @click="retryPermission"
          :disabled="isLoading"
        >
          <span :class="{ 'animate-spin': isLoading }" class="!text-xl"
            >🔄</span
          >
          {{ isLoading ? 'Vérification...' : "J'ai activé la localisation" }}
        </button>

        <button
          v-if="showAlternativeOption"
          type="button"
          class="!w-full !flex !items-center !justify-center !gap-3 !px-8 !py-4 !bg-gray-100 !text-gray-700 !rounded-xl !font-semibold !text-lg !transition-colors !border !border-gray-300 !hover:bg-gray-200 !cursor-pointer"
          @click="useAlternativeMethod"
        >
          <span class="!text-xl">🔍</span>
          Utiliser la recherche manuelle
        </button>
      </div>

      <div class="!mt-8 !pt-6 !border-t !border-gray-200">
        <p class="!text-gray-600 !flex !items-center !justify-center !gap-2">
          <span class="!text-xl">❓</span>
          Besoin d'aide ?
          <a
            href="#"
            @click.prevent="showSupport"
            class="!text-blue-600 !hover:text-blue-700 !underline !font-medium !ml-1"
          >
            Contactez le support
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  checkGeolocationPermission,
  getBrowserInfo,
} from '../utils/permissionUtils.js'

/**
 * Détermine si on est en mode Capacitor
 */
function isCapacitorMode() {
  return window.isCapacitorApp === true
}

const props = defineProps({
  description: {
    type: String,
    default:
      "Pour utiliser cette application, vous devez autoriser l'accès à votre position. La localisation nous permet de vous offrir les meilleurs services près de chez vous.",
  },
  showAlternativeOption: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['permissionGranted', 'useAlternative', 'showSupport'])

const isLoading = ref(false)
const browserInfo = ref({})

// En mode Capacitor, on ne doit jamais afficher cet écran
// car les permissions sont gérées par le SDK natif
if (isCapacitorMode()) {
  emit('permissionGranted')
}

const retryPermission = async () => {
  isLoading.value = true

  try {
    // Attendre un peu pour que l'utilisateur ait le temps d'activer la permission
    await new Promise(resolve => setTimeout(resolve, 1000))

    const permissionState = await checkGeolocationPermission()

    if (permissionState === 'granted') {
      emit('permissionGranted')
    } else {
      // Toujours refusé, on peut montrer un message plus explicite
      showStillBlockedMessage()
    }
  } catch (error) {
    console.error('Erreur lors de la vérification des permissions:', error)
  } finally {
    isLoading.value = false
  }
}

const showStillBlockedMessage = () => {
  // On pourrait émettre un événement pour afficher une notification
  console.log('Permission toujours refusée')
}

const useAlternativeMethod = () => {
  emit('useAlternative')
}

const showSupport = () => {
  emit('showSupport')
}

onMounted(() => {
  browserInfo.value = getBrowserInfo()
})
</script>
